import { For, Show, createMemo, createSignal } from "solid-js";

import {
  useAccounts,
  useContacts,
  useCustomObjects,
  useCustomRecords,
  useDeals,
} from "~/lib/hooks";
import type { AttachableEntity, NoteAttachmentRef } from "~/lib/types";

type Props = {
  wsKey: () => string;
  // Refs already attached primarily — used to exclude from the picker so
  // users can't add the primary entity as a "secondary" too.
  primary?: () => NoteAttachmentRef | null;
  value: () => NoteAttachmentRef[];
  onChange: (next: NoteAttachmentRef[]) => void;
};

// Adds secondary attachments to a note. Pick an entity type, search, click
// to attach. Already-attached items render as removable chips above the
// search input.
export function EntityAttachmentPicker(props: Props) {
  const [entityType, setEntityType] = createSignal<AttachableEntity>("contact");
  const [query, setQuery] = createSignal("");
  // Only meaningful when entityType === "custom_record"; chooses which
  // custom object's records the search queries.
  const [customCodId, setCustomCodId] = createSignal<string>("");

  const accountResults = useAccounts(props.wsKey, () => ({
    q: entityType() === "account" ? query() || undefined : undefined,
    limit: entityType() === "account" ? 8 : 1,
  }));
  const contactResults = useContacts(props.wsKey, () => ({
    q: entityType() === "contact" ? query() || undefined : undefined,
    limit: entityType() === "contact" ? 8 : 1,
  }));
  const dealResults = useDeals(props.wsKey, () => ({
    q: entityType() === "deal" ? query() || undefined : undefined,
    limit: entityType() === "deal" ? 8 : 1,
  }));
  // List of custom objects for the second dropdown.
  const customObjects = useCustomObjects(props.wsKey);
  // Records of the currently-selected custom object. Gated on customCodId so
  // we don't fire a request before the user picks one.
  const customRecordResults = useCustomRecords(
    props.wsKey,
    () => customCodId(),
    () => ({
      q: entityType() === "custom_record" ? query() || undefined : undefined,
    }),
  );

  const attached = createMemo(() => {
    const m = new Map<string, NoteAttachmentRef>();
    const p = props.primary?.();
    if (p) m.set(`${p.entity_type}:${p.entity_id}`, p);
    for (const r of props.value()) m.set(`${r.entity_type}:${r.entity_id}`, r);
    return m;
  });

  const isAttached = (et: AttachableEntity, id: string) => attached().has(`${et}:${id}`);

  const isPrimary = (et: AttachableEntity, id: string) =>
    props.primary?.()?.entity_type === et && props.primary?.()?.entity_id === id;

  const add = (et: AttachableEntity, id: string) => {
    if (isAttached(et, id)) return;
    props.onChange([...props.value(), { entity_type: et, entity_id: id }]);
    setQuery("");
  };

  const remove = (ref: NoteAttachmentRef) => {
    props.onChange(
      props
        .value()
        .filter((r) => !(r.entity_type === ref.entity_type && r.entity_id === ref.entity_id)),
    );
  };

  const labelOf = (et: AttachableEntity, id: string): string => {
    if (et === "account") {
      const m = accountResults.data?.items.find((x) => x.id === id);
      return m ? m.name : id.slice(0, 14);
    }
    if (et === "contact") {
      const m = contactResults.data?.items.find((x) => x.id === id);
      return m ? (m.display_name ?? m.full_name ?? id.slice(0, 14)) : id.slice(0, 14);
    }
    if (et === "deal") {
      const m = dealResults.data?.items.find((x) => x.id === id);
      return m ? m.name : id.slice(0, 14);
    }
    // custom_record — only resolvable when the user has the right object
    // picked; otherwise we fall back to the id slice. The chip persists
    // across saves and may be reloaded against a different cod, so a
    // missing label here is normal and the truncated id keeps the chip
    // distinguishable.
    const m = customRecordResults.data?.items.find((x) => x.id === id);
    return m ? m.name : id.slice(0, 14);
  };

  return (
    <div>
      <Show when={props.value().length > 0}>
        <div class="flex flex-wrap gap-1 mb-2">
          <For each={props.value()}>
            {(ref) => (
              <span class="badge badge-outline gap-1 pl-2">
                <span class="opacity-60">{ref.entity_type}:</span>
                {labelOf(ref.entity_type, ref.entity_id)}
                <button
                  type="button"
                  class="btn btn-ghost btn-xs btn-circle"
                  aria-label="Remove attachment"
                  onClick={() => remove(ref)}
                >
                  ✕
                </button>
              </span>
            )}
          </For>
        </div>
      </Show>
      <div class="flex gap-2 flex-wrap">
        <select
          class="select select-bordered select-sm"
          aria-label="Attachment kind"
          value={entityType()}
          onChange={(e) => {
            setEntityType(e.currentTarget.value as AttachableEntity);
            setQuery("");
          }}
        >
          <option value="contact">Contact</option>
          <option value="account">Account</option>
          <option value="deal">Deal</option>
          <Show when={(customObjects.data?.items.length ?? 0) > 0}>
            <option value="custom_record">Custom record</option>
          </Show>
        </select>
        <Show when={entityType() === "custom_record"}>
          <select
            class="select select-bordered select-sm"
            aria-label="Which custom object"
            value={customCodId()}
            onChange={(e) => {
              setCustomCodId(e.currentTarget.value);
              setQuery("");
            }}
          >
            <option value="">— pick object —</option>
            <For each={customObjects.data?.items.filter((o) => !o.archived_at) ?? []}>
              {(co) => <option value={co.id}>{co.label_singular}</option>}
            </For>
          </select>
        </Show>
        <input
          type="search"
          class="input input-bordered input-sm flex-1"
          placeholder={
            entityType() === "custom_record"
              ? customCodId()
                ? "Search records…"
                : "Pick an object first"
              : `Search ${entityType()}s…`
          }
          aria-label={`Search ${entityType()}s`}
          value={query()}
          onInput={(e) => setQuery(e.currentTarget.value)}
          disabled={entityType() === "custom_record" && !customCodId()}
        />
      </div>
      <Show when={query().length > 0}>
        <ul class="mt-2 max-h-40 overflow-y-auto divide-y divide-base-300 bg-base-100 rounded-lg border border-base-300">
          <Show when={entityType() === "account"}>
            <For each={accountResults.data?.items ?? []}>
              {(a) => (
                <li>
                  <button
                    type="button"
                    class="w-full text-left px-3 py-2 hover:bg-base-200 disabled:opacity-60"
                    disabled={isAttached("account", a.id)}
                    onClick={() => add("account", a.id)}
                  >
                    <span class="font-medium">{a.name}</span>
                    <Show when={isPrimary("account", a.id)}>
                      <span class="badge badge-ghost badge-xs ml-2">primary</span>
                    </Show>
                    <Show when={isAttached("account", a.id) && !isPrimary("account", a.id)}>
                      <span class="badge badge-ghost badge-xs ml-2">already attached</span>
                    </Show>
                  </button>
                </li>
              )}
            </For>
          </Show>
          <Show when={entityType() === "contact"}>
            <For each={contactResults.data?.items ?? []}>
              {(c) => (
                <li>
                  <button
                    type="button"
                    class="w-full text-left px-3 py-2 hover:bg-base-200 disabled:opacity-60"
                    disabled={isAttached("contact", c.id)}
                    onClick={() => add("contact", c.id)}
                  >
                    <span class="font-medium">{c.display_name ?? c.full_name ?? "Unnamed"}</span>
                    <Show when={isPrimary("contact", c.id)}>
                      <span class="badge badge-ghost badge-xs ml-2">primary</span>
                    </Show>
                    <Show when={isAttached("contact", c.id) && !isPrimary("contact", c.id)}>
                      <span class="badge badge-ghost badge-xs ml-2">already attached</span>
                    </Show>
                  </button>
                </li>
              )}
            </For>
          </Show>
          <Show when={entityType() === "deal"}>
            <For each={dealResults.data?.items ?? []}>
              {(d) => (
                <li>
                  <button
                    type="button"
                    class="w-full text-left px-3 py-2 hover:bg-base-200 disabled:opacity-60"
                    disabled={isAttached("deal", d.id)}
                    onClick={() => add("deal", d.id)}
                  >
                    <span class="font-medium">{d.name}</span>
                    <Show when={isPrimary("deal", d.id)}>
                      <span class="badge badge-ghost badge-xs ml-2">primary</span>
                    </Show>
                    <Show when={isAttached("deal", d.id) && !isPrimary("deal", d.id)}>
                      <span class="badge badge-ghost badge-xs ml-2">already attached</span>
                    </Show>
                  </button>
                </li>
              )}
            </For>
          </Show>
          <Show when={entityType() === "custom_record" && customCodId()}>
            <For each={customRecordResults.data?.items ?? []}>
              {(r) => (
                <li>
                  <button
                    type="button"
                    class="w-full text-left px-3 py-2 hover:bg-base-200 disabled:opacity-60"
                    disabled={isAttached("custom_record", r.id)}
                    onClick={() => add("custom_record", r.id)}
                  >
                    <span class="font-medium">{r.name}</span>
                    <Show when={isPrimary("custom_record", r.id)}>
                      <span class="badge badge-ghost badge-xs ml-2">primary</span>
                    </Show>
                    <Show
                      when={isAttached("custom_record", r.id) && !isPrimary("custom_record", r.id)}
                    >
                      <span class="badge badge-ghost badge-xs ml-2">already attached</span>
                    </Show>
                  </button>
                </li>
              )}
            </For>
          </Show>
        </ul>
      </Show>
    </div>
  );
}
